import { DefaultButton, DialogFooter, Dropdown, FontIcon, IDropdownOption, Modal, PrimaryButton, SelectableOptionMenuItemType, TooltipHost } from '@fluentui/react';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { IAgentReport } from '../models/reports.internal';
import { useGetBreakNamesQuery } from '../redux/apis/blenderApi';
import { usePushAgentToBreakMutation, usePushAgentToIdleMutation, usePushAgentToLoggedOutMutation } from '../redux/apis/manageAgentApi';
import { IconAndTextContainer, ModalHeader } from '../shared/styled-components';

interface IProps {
  isOpen: boolean;
  agent: IAgentReport;
  onSubmit(state: string): void;
  onDismiss(): void;
}

const ChangeStateModal: FC<IProps> = ({ isOpen, agent, onSubmit, onDismiss }) => {
  const { semanticColors } = useTheme();
  const { id: agentId, name, currentState, breakName: currentBreak } = agent;

  const { stateColor, stateText } = useMemo(
    () => {
      switch (currentState) {
        case 'Idle':
          return { stateColor: semanticColors.statusAvailable, stateText: 'Available' };
        case 'Break':
          return { stateColor: semanticColors.statusBreak, stateText: currentBreak ?? 'On break' };
        case 'Busy':
          return { stateColor: semanticColors.statusBusy, stateText: 'Busy' };
        case 'LoggedOut':
          return { stateColor: semanticColors.statusLoggedOut, stateText: 'Logged out' };
      }
    },
    [currentBreak, currentState, semanticColors.statusAvailable, semanticColors.statusBreak, semanticColors.statusBusy, semanticColors.statusLoggedOut],
  );

  const [selectedState, setSelectedState] = useState<string>();

  const { data } = useGetBreakNamesQuery();
  const breakNames = useMemo(
    () => data ?? [],
    [data],
  );

  const options = useMemo<IDropdownOption[]>(
    () => [
      ...(currentState === 'Break' ? [{ key: 'Idle', text: 'Available' }, { key: 'divider-0', text: '', itemType: SelectableOptionMenuItemType.Divider }] : []),
      ...breakNames.filter(bn => bn !== currentBreak).map(bn => ({ key: bn, text: bn, title: bn })),
      ...(currentState !== 'Busy' ? [{ key: 'divider-1', text: '', itemType: SelectableOptionMenuItemType.Divider }, { key: 'LoggedOut', text: 'Logged out' }] : []),
    ],
    [breakNames, currentBreak, currentState],
  );

  const [pushAgentToIdle] = usePushAgentToIdleMutation();
  const [pushAgentToBreak] = usePushAgentToBreakMutation();
  const [pushAgentToLoggedOut] = usePushAgentToLoggedOutMutation();

  const changeAgentState = useCallback(
    (state: string) => {
      if (state === 'Idle')
        pushAgentToIdle(agentId);
      else if (state === 'LoggedOut')
        pushAgentToLoggedOut(agentId);
      else
        pushAgentToBreak({ agentId, breakName: state });

      onSubmit(state);
      setSelectedState(undefined);
    },
    [agentId, onSubmit, pushAgentToBreak, pushAgentToIdle, pushAgentToLoggedOut],
  );

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={false}
      styles={{ main: { width: 350, padding: '1rem' }, scrollableContent: { overflowY: 'unset' } }}
    >
      <ModalHeader title="Request state change" onCancel={onDismiss} />
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontWeight: 500 }}>{name}</div>
        <IconAndTextContainer>
          <FontIcon iconName='CircleFill' style={{ color: stateColor, marginTop: 1, marginRight: '0.5rem' }} />
          <TooltipHost content={stateText}>{stateText}</TooltipHost>
        </IconAndTextContainer>
      </div>
      <Dropdown
        label="Change state to"
        selectedKey={selectedState}
        options={options}
        onChange={(_, option) => option ? setSelectedState(option.key as string) : undefined}
      />
      <DialogFooter>
        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
        <PrimaryButton disabled={!selectedState || agent.currentState === 'LoggedOut'} onClick={() => changeAgentState(selectedState!)} text="Change state" />
        <DefaultButton onClick={onDismiss} text="Cancel" />
      </DialogFooter>
    </Modal>
  );
};

export default ChangeStateModal;